import { useCallback, useEffect, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { html as htmlLang } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";

const CODE_MIRROR_EXTENSIONS = [htmlLang(), EditorView.lineWrapping];
const COMMIT_DEBOUNCE_MS = 300;

export function EmailCodeEditor({
  html,
  onLiveChange,
  onCommit,
}: {
  html: string;
  onLiveChange: (html: string) => void;
  onCommit: (html: string) => void;
}) {
  const [draft, setDraft] = useState(html);
  const draftRef = useRef(draft);
  const htmlPropRef = useRef(html);
  const onLiveChangeRef = useRef(onLiveChange);
  const onCommitRef = useRef(onCommit);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextPropSyncRef = useRef(false);

  draftRef.current = draft;
  onLiveChangeRef.current = onLiveChange;
  onCommitRef.current = onCommit;

  useEffect(() => {
    if (html === htmlPropRef.current) return;
    htmlPropRef.current = html;

    if (skipNextPropSyncRef.current) {
      skipNextPropSyncRef.current = false;
      return;
    }

    setDraft(html);
    draftRef.current = html;
  }, [html]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const latest = draftRef.current;
      if (latest !== htmlPropRef.current) {
        htmlPropRef.current = latest;
        onCommitRef.current(latest);
      }
    };
  }, []);

  const handleChange = useCallback((value: string) => {
    setDraft(value);
    draftRef.current = value;
    onLiveChangeRef.current(value);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      skipNextPropSyncRef.current = true;
      htmlPropRef.current = value;
      onCommitRef.current(value);
    }, COMMIT_DEBOUNCE_MS);
  }, []);

  return (
    <CodeMirror
      value={draft}
      height="100%"
      extensions={CODE_MIRROR_EXTENSIONS}
      onChange={handleChange}
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        highlightActiveLine: true,
        bracketMatching: true,
        closeBrackets: true,
        autocompletion: true,
      }}
      className="h-full text-sm"
    />
  );
}
